import React, { useState, useEffect } from 'react';
import config from '../config';
import './DevDataPage.css';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getAuth } from 'firebase/auth';

const DevDataPage = ({ user, unityAuthData, cloudSaveData }) => {
  const [userData, setUserData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [designsData, setDesignsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('user');
  
  // Use the subscription context
  const { 
    subscriptionData: contextSubscriptionData, 
    refreshSubscription, 
    loading: subscriptionLoading 
  } = useSubscription();

  // Debug logs
  useEffect(() => {
    console.log('DevDataPage: Context subscription data:', contextSubscriptionData);
    console.log('DevDataPage: Local subscription data state:', subscriptionData);
  }, [contextSubscriptionData, subscriptionData]);

  useEffect(() => {
    // Set the initial data from props
    if (user) {
      setUserData({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        providerData: user.providerData,
        metadata: {
          creationTime: user.metadata?.creationTime,
          lastSignInTime: user.metadata?.lastSignInTime
        }
      });
    }

    // Extract subscription data from cloudSaveData if available
    if (cloudSaveData?.subscription) {
      console.log('Setting subscription data from cloudSaveData:', cloudSaveData.subscription);
      setSubscriptionData(cloudSaveData.subscription);
    }
  }, [user, cloudSaveData]);

  // Update local subscription data when context subscription data changes
  useEffect(() => {
    if (contextSubscriptionData) {
      console.log('Updating subscription data from context:', contextSubscriptionData);
      setSubscriptionData(contextSubscriptionData);
    }
  }, [contextSubscriptionData]);

  // Helper function to get authentication headers with both Firebase and Unity tokens
  const getAuthHeaders = async () => {
    // Get Firebase token
    const auth = getAuth();
    const currentUser = auth.currentUser;
    let firebaseToken = '';
    
    if (currentUser) {
      try {
        // Always force a token refresh to ensure we have a fresh one
        firebaseToken = await currentUser.getIdToken(true);
        console.log('Got fresh Firebase token for API request');
      } catch (error) {
        console.error('Error getting Firebase token:', error);
      }
    } else {
      console.warn('No Firebase user is logged in');
    }
    
    // Include both Firebase and Unity tokens in headers
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': firebaseToken ? `Bearer ${firebaseToken}` : '',
      'X-Unity-Token': unityAuthData?.accessToken || ''
    };
    
    return headers;
  };

  const refreshCloudData = async () => {
    if (!unityAuthData?.accessToken || !unityAuthData?.playerId) {
      setError("Missing Unity authentication data");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = config.backendUrl;
      const response = await fetch(`${API_BASE_URL}/cloud-save/${unityAuthData.playerId}`, {
        headers: {
          'Authorization': `Bearer ${unityAuthData.accessToken}`,
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch cloud save data: ${errorText}`);
      }

      const data = await response.json();
      
      // Update all our state with the fetched data
      setUserData(prev => ({ ...prev, cloudSaveData: data }));
      
      if (data.subscription) {
        setSubscriptionData(data.subscription);
      }
    } catch (error) {
      console.error('Error fetching cloud save data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDesignsData = async () => {
    if (!unityAuthData?.accessToken || !unityAuthData?.playerId) {
      setError("Missing Unity authentication data");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = config.backendUrl;
      const response = await fetch(`${API_BASE_URL}/designs/${unityAuthData.playerId}`, {
        headers: {
          'Authorization': `Bearer ${unityAuthData.accessToken}`,
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch designs data: ${errorText}`);
      }

      const data = await response.json();
      setDesignsData(data);
    } catch (error) {
      console.error('Error fetching designs data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionData = async () => {
    if (!unityAuthData?.playerId) {
      setError("Missing Unity player ID");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('Calling refreshSubscription from context');
      const data = await refreshSubscription();
      console.log('Refresh subscription result:', data);
      
      // Directly update the local state in case the context update is not triggering
      if (data) {
        setSubscriptionData(data);
      } else {
        // Fallback to direct API call if context method returns no data
        console.log('No data from context refresh, falling back to direct API call');
        const API_BASE_URL = config.backendUrl;
        
        // Get proper authentication headers
        const headers = await getAuthHeaders();
        console.log('Using headers for API call:', headers);
        
        // Add cache-busting timestamp to URL
        const now = Date.now();
        const cacheBusterUrl = `${API_BASE_URL}/subscription/${unityAuthData.playerId}?_t=${now}`;
        
        const response = await fetch(cacheBusterUrl, { headers });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch subscription data: ${errorText}`);
        }

        const apiData = await response.json();
        console.log('Direct API call result:', apiData);
        setSubscriptionData(apiData);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // If not in development environment, don't render anything
  if (!config.isDevelopment) {
    return null;
  }

  return (
    <div className="dev-data-container">
      <h1 className="dev-data-title">Development Data Explorer</h1>
      <p className="dev-data-subtitle">This page is only visible in development environment</p>
      
      <div className="dev-data-tabs">
        <button 
          className={`dev-data-tab ${activeTab === 'user' ? 'active' : ''}`}
          onClick={() => setActiveTab('user')}
        >
          User Data
        </button>
        <button 
          className={`dev-data-tab ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('subscription');
            // Try to fetch subscription data when the tab is clicked
            if (!subscriptionData) {
              fetchSubscriptionData();
            }
          }}
        >
          Subscription Data
        </button>
        <button 
          className={`dev-data-tab ${activeTab === 'unity' ? 'active' : ''}`}
          onClick={() => setActiveTab('unity')}
        >
          Unity Auth Data
        </button>
        <button 
          className={`dev-data-tab ${activeTab === 'cloud' ? 'active' : ''}`}
          onClick={() => setActiveTab('cloud')}
        >
          Cloud Save Data
        </button>
        <button 
          className={`dev-data-tab ${activeTab === 'designs' ? 'active' : ''}`}
          onClick={() => setActiveTab('designs')}
        >
          Designs Data
        </button>
      </div>
      
      <div className="dev-data-actions">
        <button 
          className="dev-data-button"
          onClick={refreshCloudData}
          disabled={loading}
        >
          Refresh Cloud Data
        </button>
        <button 
          className="dev-data-button"
          onClick={fetchDesignsData}
          disabled={loading}
        >
          Fetch Designs Data
        </button>
        <button 
          className="dev-data-button"
          onClick={fetchSubscriptionData}
          disabled={loading}
        >
          Fetch Subscription Data
        </button>
      </div>
      
      {loading && <div className="dev-data-loading">Loading data...</div>}
      {error && <div className="dev-data-error">Error: {error}</div>}
      
      <div className="dev-data-content">
        {activeTab === 'user' && (
          userData ? (
            <div className="dev-data-json">
              <h2>User Data</h2>
              <pre>{JSON.stringify(userData, null, 2)}</pre>
            </div>
          ) : (
            <div className="dev-data-empty">No user data available</div>
          )
        )}
        
        {activeTab === 'subscription' && (
          <div className={subscriptionData ? "dev-data-json" : "dev-data-empty"}>
            {subscriptionData ? (
              <>
                <h2>Subscription Data</h2>
                <pre>{JSON.stringify(subscriptionData, null, 2)}</pre>
              </>
            ) : (
              <div>
                No subscription data available. 
                <button 
                  onClick={fetchSubscriptionData} 
                  className="dev-data-inline-button"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Fetch Subscription Data'}
                </button>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'unity' && (
          unityAuthData ? (
            <div className="dev-data-json">
              <h2>Unity Auth Data</h2>
              <pre>{JSON.stringify(unityAuthData, null, 2)}</pre>
            </div>
          ) : (
            <div className="dev-data-empty">No Unity auth data available</div>
          )
        )}
        
        {activeTab === 'cloud' && (
          cloudSaveData ? (
            <div className="dev-data-json">
              <h2>Cloud Save Data</h2>
              <pre>{JSON.stringify(cloudSaveData, null, 2)}</pre>
            </div>
          ) : (
            <div className="dev-data-empty">No cloud save data available</div>
          )
        )}
        
        {activeTab === 'designs' && (
          designsData ? (
            <div className="dev-data-json">
              <h2>Designs Data</h2>
              <pre>{JSON.stringify(designsData, null, 2)}</pre>
            </div>
          ) : (
            <div className="dev-data-empty">No designs data available</div>
          )
        )}
      </div>
    </div>
  );
};

export default DevDataPage;
